import React, { useMemo, useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  Steps,
  Typography,
  Table,
  Alert,
  Space,
  Progress,
  Result,
  Divider,
  Card,
  Row,
  Col,
  Tag,
  message,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
}

interface PreviewItem {
  row: number;
  customerName: string;
  customerCode: string;
  productNameEN: string;
  productNameCN: string;
  skuId: string;
  quantityPackage: number;
  quantityBaseUnit: number;
  unitPrice: number;
  courier: string;
  receiver: string;
  deliveryAddress: string;
  status: 'valid' | 'error' | 'warning';
  errors: ValidationError[];
}

interface BulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onUploadComplete: (data: any[]) => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  visible,
  onCancel,
  onUploadComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 模擬的CSV模板數據
  const templateData = [
    {
      customerName: '客戶A',
      customerCode: 'CUST001',
      productNameEN: 'Wireless Headphones',
      productNameCN: '無線耳機',
      skuId: 'WH-001',
      barcodePackage: '1234567890123',
      barcodeBaseUnit: '1234567890124',
      quantityPackage: '10',
      quantityBaseUnit: '10',
      brand: 'TechBrand',
      length: '20',
      width: '15',
      height: '8',
      weight: '0.5',
      unitPrice: '99.99',
      courier: 'DHL',
      receiver: '李先生',
      countryCode: 'CN',
      deliveryAddress: '廣東省深圳市南山區科技園',
      deliveryTrackingNumber: '',
      deliveryRemark: '',
      allocateRule: 'FIFO',
      remark: '測試訂單',
      status: '待發貨'
    }
  ];

  // 字段說明
  const fieldDescriptions = [
    { field: 'customerName', name: '客戶名稱', required: true, description: '客戶的完整名稱' },
    { field: 'customerCode', name: '客戶代碼', required: true, description: '客戶的唯一識別代碼' },
    { field: 'productNameEN', name: '產品名稱(英文)', required: true, description: '產品的英文名稱' },
    { field: 'productNameCN', name: '產品名稱(中文)', required: true, description: '產品的中文名稱' },
    { field: 'skuId', name: 'SKU ID', required: true, description: '產品的SKU識別碼' },
    { field: 'barcodePackage', name: '包裝條碼', required: false, description: '包裝級別的條碼' },
    { field: 'barcodeBaseUnit', name: '基本單位條碼', required: false, description: '基本單位的條碼' },
    { field: 'quantityPackage', name: '包裝數量', required: true, description: '包裝單位的數量' },
    { field: 'quantityBaseUnit', name: '基本單位數量', required: true, description: '基本單位的數量' },
    { field: 'brand', name: '品牌', required: false, description: '產品品牌' },
    { field: 'length', name: '長度', required: false, description: '產品長度(cm)' },
    { field: 'width', name: '寬度', required: false, description: '產品寬度(cm)' },
    { field: 'height', name: '高度', required: false, description: '產品高度(cm)' },
    { field: 'weight', name: '重量', required: false, description: '產品重量(kg)' },
    { field: 'unitPrice', name: '單價', required: true, description: '產品單價' },
    { field: 'courier', name: '快遞商', required: true, description: '配送快遞公司' },
    { field: 'receiver', name: '收件人', required: true, description: '收件人姓名' },
    { field: 'countryCode', name: '國家或地區', required: true, description: '收件地址國家或地區代碼' },
    { field: 'deliveryAddress', name: '配送地址', required: true, description: '詳細配送地址' },
    { field: 'deliveryTrackingNumber', name: '追蹤號', required: false, description: '快遞追蹤號碼' },
    { field: 'deliveryRemark', name: '配送備註', required: false, description: '配送相關備註' },
    { field: 'allocateRule', name: '分配規則', required: false, description: 'FIFO/LIFO/FEFO/MANUAL' },
    { field: 'remark', name: '備註', required: false, description: '訂單備註' },
    { field: 'status', name: '狀態', required: false, description: '訂單狀態' },
  ];

  // 可選字段勾選狀態（依賴 fieldDescriptions）
  const optionalDefaultKeys = useMemo(
    () => fieldDescriptions.filter(f => !f.required).map(f => f.field),
    [fieldDescriptions]
  );
  const [selectedOptionalKeys, setSelectedOptionalKeys] = useState<string[]>(optionalDefaultKeys);

  // 下載模板
  const downloadTemplate = () => {
    // 必填在前，可選依勾選
    const required = fieldDescriptions.filter(f => f.required);
    const optional = fieldDescriptions.filter(f => !f.required && selectedOptionalKeys.includes(f.field));
    const selected = [...required, ...optional];
    const headers = selected.map(field => field.name).join(',');
    const sampleRow = templateData.map(row => selected.map(field => row[field.field] || '').join(',')).join('\n');
    const csvContent = `${headers}\n${sampleRow}`;
    
    // 創建並下載文件
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', '出庫訂單批量上傳模板.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('模板下載成功');
  };

  // 文件上傳配置
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file) => {
      setUploadedFile(file);
      processFile(file);
      return false;
    },
  };

  // 處理文件
  const processFile = async (file: UploadFile) => {
    setIsProcessing(true);
    setUploadProgress(0);

    try {
      // 读取文件內容
      const fileContent = await readFileContent(file);
      setUploadProgress(30);

      // 解析CSV數據
      const parsedData = parseCSVContent(fileContent);
      setUploadProgress(60);

      // 驗證數據
      const validatedData = validateParsedData(parsedData);
      setUploadProgress(90);

      // 生成驗證錯誤列表
      const errors = generateValidationErrors(validatedData);
      
      setPreviewData(validatedData);
      setValidationErrors(errors);
      setUploadProgress(100);
      
      setIsProcessing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('文件處理錯誤:', error);
      message.error('文件處理失敗，請檢查文件格式');
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // 读取文件內容
  const readFileContent = (file: UploadFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => reject(new Error('文件读取失敗'));
      reader.readAsText(file as File, 'UTF-8');
    });
  };

  // 解析CSV內容
  const parseCSVContent = (content: string): any[] => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正確，至少需要標題行和一行數據');
    }

    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === headers.length) {
        const row: any = { row: i };
        headers.forEach((header, index) => {
          row[getFieldKey(header)] = values[index]?.trim() || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  // 解析CSV行（處理引號和逗號）
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result.map(val => val.replace(/"/g, ''));
  };

  // 将中文字段名映射到字段键
  const getFieldKey = (header: string): string => {
    const fieldMap: Record<string, string> = {
      '客戶名稱': 'customerName',
      '客戶代碼': 'customerCode',
      '產品名稱(英文)': 'productNameEN',
      '產品名稱(中文)': 'productNameCN',
      'SKU ID': 'skuId',
      '包裝條碼': 'barcodePackage',
      '基本單位條碼': 'barcodeBaseUnit',
      '包裝數量': 'quantityPackage',
      '基本單位數量': 'quantityBaseUnit',
      '品牌': 'brand',
      '長度': 'length',
      '寬度': 'width',
      '高度': 'height',
      '重量': 'weight',
      '單價': 'unitPrice',
      '快遞商': 'courier',
      '收件人': 'receiver',
      '國家或地區': 'countryCode',
      '配送地址': 'deliveryAddress',
      '追蹤號': 'deliveryTrackingNumber',
      '配送備註': 'deliveryRemark',
      '分配規則': 'allocateRule',
      '備註': 'remark',
      '狀態': 'status'
    };
    return fieldMap[header] || header;
  };

  // 驗證解析的數據
  const validateParsedData = (data: any[]): PreviewItem[] => {
    return data.map((row, index) => {
      const errors: ValidationError[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      // 必填字段驗證
      const requiredFields = [
        { key: 'customerName', name: '客戶名稱' },
        { key: 'customerCode', name: '客戶代碼' },
        { key: 'productNameEN', name: '產品名稱(英文)' },
        { key: 'productNameCN', name: '產品名稱(中文)' },
        { key: 'skuId', name: 'SKU ID' },
        { key: 'quantityPackage', name: '包裝數量' },
        { key: 'quantityBaseUnit', name: '基本單位數量' },
        { key: 'unitPrice', name: '單價' }
      ];

      requiredFields.forEach(field => {
        if (!row[field.key] || row[field.key].toString().trim() === '') {
          errors.push({
            row: index + 1,
            field: field.key,
            value: row[field.key],
            error: `${field.name}不能為空`,
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 數字字段驗證
      const numberFields = ['quantityPackage', 'quantityBaseUnit', 'unitPrice'];
      numberFields.forEach(field => {
        if (row[field] && isNaN(Number(row[field]))) {
          errors.push({
            row: index + 1,
            field: field,
            value: row[field],
            error: `${field}必須為數字`,
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 庫存警告（模拟）
      if (row.quantityBaseUnit && Number(row.quantityBaseUnit) > 30) {
        errors.push({
          row: index + 1,
          field: 'quantityBaseUnit',
          value: row.quantityBaseUnit,
          error: '庫存可能不足，建議檢查',
          severity: 'warning'
        });
        if (status !== 'error') status = 'warning';
      }

      return {
        row: index + 1,
        customerName: row.customerName || '',
        customerCode: row.customerCode || '',
        productNameEN: row.productNameEN || '',
        productNameCN: row.productNameCN || '',
        skuId: row.skuId || '',
        quantityPackage: Number(row.quantityPackage) || 0,
        quantityBaseUnit: Number(row.quantityBaseUnit) || 0,
        unitPrice: Number(row.unitPrice) || 0,
        courier: row.courier || '',
        receiver: row.receiver || '',
        deliveryAddress: row.deliveryAddress || '',
        status,
        errors
      };
    });
  };

  // 生成驗證錯誤列表
  const generateValidationErrors = (data: PreviewItem[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    data.forEach(item => {
      errors.push(...item.errors);
    });
    return errors;
  };





  // 確認導入
  const confirmImport = () => {
    const validData = previewData.filter(item => item.status === 'valid' || item.status === 'warning');
    
    if (validData.length === 0) {
      message.error('沒有有效的數據可以導入');
      return;
    }

    // 轉換為出庫訂單格式
    const orders = validData.map(item => ({
      customerName: item.customerName,
      customerCode: item.customerCode,
      items: [{
        productNameEN: item.productNameEN,
        productNameCN: item.productNameCN,
        skuId: item.skuId,
        quantityPackage: item.quantityPackage,
        quantityBaseUnit: item.quantityBaseUnit,
        unitPrice: item.unitPrice,
      }],
      courier: item.courier,
      receiver: item.receiver,
      deliveryAddress: item.deliveryAddress,
      status: '待發貨',
      hasStockIssues: item.status === 'warning'
    }));

    onUploadComplete(orders);
    setCurrentStep(2);
    
    setTimeout(() => {
      handleClose();
      message.success(`成功導入 ${validData.length} 個訂單`);
    }, 2000);
  };

  // 關閉彈窗
  const handleClose = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setIsProcessing(false);
    setUploadProgress(0);
    onCancel();
  };

  // 預覽表格列定義
  const previewColumns: ColumnsType<PreviewItem> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
      render: (row, record) => (
        <Space>
          <span>{row}</span>
          {record.status === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          {record.status === 'warning' && <ExclamationCircleOutlined style={{ color: '#faad14' }} />}
          {record.status === 'valid' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
        </Space>
      ),
    },
    {
      title: '客戶',
      key: 'customer',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.customerName || <Text type="danger">缺少客戶名稱</Text>}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.customerCode}</Text>
        </div>
      ),
    },
    {
      title: '產品',
      key: 'product',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.productNameEN}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.productNameCN}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.skuId}</div>
        </div>
      ),
    },
    {
      title: '數量',
      key: 'quantity',
      width: 100,
      render: (_, record) => (
        <div>
          <div>包裝: {record.quantityPackage}</div>
          <div>基本: {record.quantityBaseUnit}</div>
        </div>
      ),
    },
    {
      title: '單價',
      dataIndex: 'unitPrice',
      width: 100,
      render: (price) => `¥${price.toFixed(2)}`,
    },
    {
      title: '收件信息',
      key: 'delivery',
      render: (_, record) => (
        <div>
          <div>{record.receiver}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.deliveryAddress.length > 20 
              ? `${record.deliveryAddress.substring(0, 20)}...` 
              : record.deliveryAddress}
          </div>
        </div>
      ),
    },
    {
      title: '狀態',
      key: 'status',
      width: 100,
      render: (_, record) => {
        let color = 'default';
        let text = '未知';
        
        switch (record.status) {
          case 'valid':
            color = 'success';
            text = '有效';
            break;
          case 'warning':
            color = 'warning';
            text = '警告';
            break;
          case 'error':
            color = 'error';
            text = '錯誤';
            break;
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  const errorColumns: ColumnsType<ValidationError> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
    },
    {
      title: '字段',
      dataIndex: 'field',
      width: 120,
    },
    {
      title: '值',
      dataIndex: 'value',
      width: 150,
      render: (value) => value || <Text type="secondary">空值</Text>,
    },
    {
      title: '錯誤信息',
      dataIndex: 'error',
      render: (error, record) => (
        <Text type={record.severity === 'error' ? 'danger' : 'warning'}>
          {error}
        </Text>
      ),
    },
    {
      title: '嚴重性',
      dataIndex: 'severity',
      width: 80,
      render: (severity) => (
        <Tag color={severity === 'error' ? 'red' : 'orange'}>
          {severity === 'error' ? '錯誤' : '警告'}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: '上傳文件',
      description: '選擇並上傳CSV/Excel文件',
    },
    {
      title: '數據預覽',
      description: '查看和驗證數據',
    },
    {
      title: '導入完成',
      description: '數據導入成功',
    },
  ];

  return (
    <Modal
      title="批量上傳出庫訂單"
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map(step => (
          <Step key={step.title} title={step.title} description={step.description} />
        ))}
      </Steps>

      {currentStep === 0 && (
        <div>
          <Alert
            message="批量上傳說明"
            description="現在支持直接上傳CSV文件，系統將自動解析和驗證數據。您也可以下載模板文件作為參考。"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title="步驟 1: 下載模板" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                請先下載出庫訂單模板文件，模板包含所有必需的字段和格式說明。支持CSV格式，請確保文件編碼為UTF-8。
              </Paragraph>
              <div style={{ 
                backgroundColor: '#f6ffed', 
                border: '1px solid #b7eb8f', 
                borderRadius: '6px', 
                padding: '12px', 
                marginBottom: '16px' 
              }}>
                <Text strong style={{ color: '#389e0d' }}>✓ 提示</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li>必填字段不能為空，否則會導致驗證失敗</li>
                  <li>日期字段請使用 YYYY-MM-DD 格式（如：2025-01-15）</li>
                  <li>數量字段請輸入有效的數字</li>
                  <li>CSV文件請保存為UTF-8編碼，避免中文亂碼</li>
                </ul>
              </div>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadTemplate}
                size="large"
              >
                下載CSV模板
              </Button>
            </Space>
          </Card>

          <Card title="步驟 2: 上傳文件">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                填寫完成後，請上傳文件進行數據驗證和預覽。
              </Paragraph>
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  size="large"
                  loading={isProcessing}
                >
                  {isProcessing ? '處理中...' : '選擇文件上傳'}
                </Button>
              </Upload>
              {uploadedFile && (
                <div>
                  <Text>已選擇文件: {uploadedFile.name}</Text>
                  {isProcessing && (
                    <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
                  )}
                </div>
              )}
            </Space>
          </Card>

          <Card title="字段說明（必填在前、可選可勾選，用於生成模板）" style={{ marginTop: 16 }}>
            <Title level={5}>必填字段</Title>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              {fieldDescriptions.filter(f => f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color="red">必填</Tag>
                    <Text strong>{field.name}</Text>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666', marginLeft: 16 }}>
                    {field.description}
                  </div>
                </Col>
              ))}
            </Row>
            <Title level={5}>可選字段（勾選後包含在模板中）</Title>
            <Row gutter={16}>
              {fieldDescriptions.filter(f => !f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space align="start">
                    <Checkbox
                      checked={selectedOptionalKeys.includes(field.field)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? Array.from(new Set([...selectedOptionalKeys, field.field]))
                          : selectedOptionalKeys.filter(k => k !== field.field);
                        setSelectedOptionalKeys(next);
                      }}
                    />
                    <div>
                      <Space>
                        <Tag>可選</Tag>
                        <Text strong>{field.name}</Text>
                      </Space>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {field.description}
                      </div>
                    </div>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message={`數據驗證完成，共 ${previewData.length} 行數據`}
            description={
              <Space>
                <span>有效: {previewData.filter(item => item.status === 'valid').length}</span>
                <span>警告: {previewData.filter(item => item.status === 'warning').length}</span>
                <span>錯誤: {previewData.filter(item => item.status === 'error').length}</span>
                <span style={{ marginLeft: '20px', color: '#666' }}>
                  | 按鈕狀態: {validationErrors.filter(e => e.severity === 'error').length > 0 || 
                             previewData.filter(item => item.status === 'valid' || item.status === 'warning').length === 0 
                             ? '🚫 已禁用' : '✅ 可用'}
                </span>
              </Space>
            }
            type={validationErrors.filter(e => e.severity === 'error').length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationErrors.length > 0 && (
            <Card 
              title={
                <Space>
                  <span>驗證錯誤</span>
                  <Tag color="red">
                    {validationErrors.filter(e => e.severity === 'error').length} 個錯誤
                  </Tag>
                  <Tag color="orange">
                    {validationErrors.filter(e => e.severity === 'warning').length} 個警告
                  </Tag>
                </Space>
              } 
              style={{ marginBottom: 16 }}
            >
              {validationErrors.filter(e => e.severity === 'error').length > 0 && (
                <Alert
                  message="存在錯誤項目"
                  description="請修正所有錯誤項目後才能進行導入。警告項目不會阻止導入，但建議檢查。"
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Table
                dataSource={validationErrors}
                columns={errorColumns}
                rowKey={(record, index) => `${record.row}-${record.field}-${index}`}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          )}

          <Card title="數據預覽" extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>重新上傳</Button>
              {(() => {
                const hasErrors = validationErrors.filter(e => e.severity === 'error').length > 0;
                const validDataCount = previewData.filter(item => item.status === 'valid' || item.status === 'warning').length;
                const isDisabled = validDataCount === 0 || hasErrors;
                
                console.log('Button state debug:', {
                  hasErrors,
                  validDataCount,
                  isDisabled,
                  validationErrors: validationErrors.length,
                  previewData: previewData.length
                });

                return (
                  <Tooltip 
                    title={
                      hasErrors
                        ? `存在 ${validationErrors.filter(e => e.severity === 'error').length} 個錯誤，請修正後重新上傳`
                        : validDataCount === 0
                        ? '沒有有效的數據可以導入'
                        : '確認導入數據'
                    }
                  >
                    <Button 
                      type={isDisabled ? 'default' : 'primary'}
                      onClick={isDisabled ? undefined : confirmImport}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? '#f5f5f5' : undefined,
                        borderColor: isDisabled ? '#d9d9d9' : undefined,
                        color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                      danger={isDisabled}
                    >
                      {isDisabled ? (
                        <>
                          確認導入
                        </>
                      ) : (
                        '確認導入'
                      )}
                    </Button>
                  </Tooltip>
                );
              })()}
            </Space>
          }>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey="row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => {
                switch (record.status) {
                  case 'error':
                    return 'row-error';
                  case 'warning':
                    return 'row-warning';
                  case 'valid':
                    return 'row-valid';
                  default:
                    return '';
                }
              }}
            />
          </Card>
        </div>
      )}

      {currentStep === 2 && (
        <Result
          status="success"
          title="批量上傳完成"
          subTitle={`成功導入 ${previewData.filter(item => item.status === 'valid' || item.status === 'warning').length} 個出庫訂單`}
          icon={<CheckCircleOutlined />}
        />
      )}

      <style>{`
        .row-error {
          background-color: #fff2f0 !important;
        }
        .row-warning {
          background-color: #fffbe6 !important;
        }
        .row-valid {
          background-color: #f6ffed !important;
        }
        
        /* 强制禁用按钮樣式 */
        .ant-btn-primary[disabled], 
        .ant-btn-primary[disabled]:hover, 
        .ant-btn-primary[disabled]:focus, 
        .ant-btn-primary[disabled]:active {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          color: #666 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        
        /* 確保禁用狀態下的樣式覆盖 */
        .ant-btn[disabled] {
          background-color: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.25) !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </Modal>
  );
};

export default BulkUploadModal;